import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useConfirm } from "../context/ConfirmContext";
import { useToast } from "../context/ToastContext";
import { getVendors, createVendor, updateVendor, deactivateVendor, reactivateVendor, getVendorStatement } from "../api";
import { LoadingOverlay } from "../components/LoadingOverlay";

const NAVY = "#0f172a";

const WHT_CATEGORIES = [
  { value: "professional_services", label: "Professional Services (3%)" },
  { value: "advertising", label: "Advertising (2%)" },
  { value: "rent", label: "Rent (5%)" },
  { value: "transport", label: "Transport (1%)" },
  { value: "other", label: "Other" },
];

const ENTITY_TYPES = [
  { value: "company", label: "Company (juristic — PND.53)" },
  { value: "individual", label: "Individual (PND.3)" },
];

const BLANK_VENDOR = {
  name: "", tax_id: "", branch_number: "00000", address: "",
  default_wht_category: "other", entity_type: "company",
  contact_name: "", contact_email: "",
};

export default function VendorsPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { toast } = useToast();

  const [showInactive, setShowInactive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(BLANK_VENDOR);
  const [statementVendorId, setStatementVendorId] = useState<number | null>(null);

  const { data: statement, isLoading: statementLoading } = useQuery({
    queryKey: ["vendor-statement", statementVendorId],
    queryFn: () => getVendorStatement(statementVendorId!),
    enabled: statementVendorId !== null,
  });

  const { data: vendors = [], isLoading, isError } = useQuery({
    queryKey: ["vendors", showInactive],
    queryFn: () => getVendors(showInactive ? { is_active: false } : undefined),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["vendors"] });

  const createMut = useMutation({
    mutationFn: createVendor,
    onSuccess: () => { invalidate(); closeForm(); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to save vendor", "error"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: object }) => updateVendor(id, data),
    onSuccess: () => { invalidate(); closeForm(); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to save vendor", "error"),
  });
  const deactivateMut = useMutation({
    mutationFn: deactivateVendor, onSuccess: invalidate,
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to deactivate vendor", "error"),
  });
  const reactivateMut = useMutation({
    mutationFn: reactivateVendor, onSuccess: invalidate,
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to reactivate vendor", "error"),
  });

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(BLANK_VENDOR);
  }

  function startEdit(v: any) {
    setForm({
      name: v.name || "", tax_id: v.tax_id || "", branch_number: v.branch_number || "00000",
      address: v.address || "", default_wht_category: v.default_wht_category || "other",
      entity_type: v.entity_type || "company", contact_name: v.contact_name || "",
      contact_email: v.contact_email || "",
    });
    setEditingId(v.id);
    setShowForm(true);
  }

  function handleSubmit() {
    if (!form.name.trim()) return;
    if (editingId) updateMut.mutate({ id: editingId, data: form });
    else createMut.mutate(form);
  }

  async function handleDeactivate(id: number) {
    if (await confirm("Deactivate this vendor? It will no longer be selectable on new bills.", { confirmLabel: "Deactivate" })) {
      deactivateMut.mutate(id);
    }
  }

  return (
    <div>
      <LoadingOverlay active={
        createMut.isPending || updateMut.isPending || deactivateMut.isPending || reactivateMut.isPending
      } />
      <div className="page-header">
        <div>
          <h1>Vendors</h1>
          <p>Suppliers and service providers the company pays</p>
        </div>
        <button onClick={() => { showForm ? closeForm() : setShowForm(true); }} style={primaryBtnStyle}>
          {showForm ? "Cancel" : "+ Add Vendor"}
        </button>
      </div>

      <div className="tab-pill-bar" style={{ marginBottom: 20 }}>
        <button onClick={() => setShowInactive(false)} className={!showInactive ? "active" : ""}>Active</button>
        <button onClick={() => setShowInactive(true)} className={showInactive ? "active" : ""}>Inactive</button>
      </div>

      {showForm && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h3 style={{ margin: "0 0 16px", color: NAVY, fontSize: 16 }}>{editingId ? "Edit Vendor" : "New Vendor"}</h3>
          <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Acme Supplies Co., Ltd." style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Tax ID</label>
              <input value={form.tax_id} onChange={e => setForm(f => ({ ...f, tax_id: e.target.value }))}
                placeholder="13-digit Thai Tax ID" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Branch Number</label>
              <input value={form.branch_number} onChange={e => setForm(f => ({ ...f, branch_number: e.target.value }))}
                placeholder="00000 = Head Office" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Entity Type</label>
              <select value={form.entity_type} onChange={e => setForm(f => ({ ...f, entity_type: e.target.value }))} style={inputStyle}>
                {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Default WHT Category</label>
              <select value={form.default_wht_category} onChange={e => setForm(f => ({ ...f, default_wht_category: e.target.value }))} style={inputStyle}>
                {WHT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Contact Name</label>
              <input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Contact Email</label>
              <input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Address</label>
            <textarea value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
              rows={2} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          <button onClick={handleSubmit} disabled={!form.name.trim() || createMut.isPending || updateMut.isPending} style={primaryBtnStyle}>
            {createMut.isPending || updateMut.isPending ? "Saving…" : editingId ? "Save Changes" : "Create Vendor"}
          </button>
        </div>
      )}

      {isLoading && <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>Loading vendors…</div>}
      {isError && <div style={{ textAlign: "center", padding: "40px 0", color: "#dc2626" }}>Something went wrong loading vendors.</div>}

      {!isLoading && !isError && (
        <div style={{ display: "grid", gap: 10 }}>
          {(vendors as any[]).map((v: any) => (
            <div key={v.id} className="vendor-card-row" style={{
              background: "white", border: "1px solid #e2e8f0", borderRadius: 12,
              padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 15, color: "#1e293b" }}>{v.name}</span>
                  <span style={{ background: "#f1f5f9", color: "#64748b", padding: "2px 8px", borderRadius: 12, fontSize: 11 }}>
                    {WHT_CATEGORIES.find(c => c.value === v.default_wht_category)?.label || v.default_wht_category}
                  </span>
                  {!v.is_active && (
                    <span style={{ background: "#fee2e2", color: "#991b1b", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                      Inactive
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#64748b" }}>
                  {v.tax_id && <span>Tax ID: {v.tax_id}</span>}
                  {v.branch_number && <span>Branch: {v.branch_number}</span>}
                  {v.contact_email && <span>{v.contact_email}</span>}
                </div>
              </div>
              <div className="vendor-card-actions" style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button onClick={() => setStatementVendorId(v.id)} style={outlineBtnStyle}>View Statement</button>
                <button onClick={() => startEdit(v)} style={outlineBtnStyle}>Edit</button>
                {v.is_active ? (
                  <button onClick={() => handleDeactivate(v.id)} disabled={deactivateMut.isPending}
                    style={{ ...outlineBtnStyle, color: "#dc2626", borderColor: "#fca5a5" }}>Deactivate</button>
                ) : (
                  <button onClick={() => reactivateMut.mutate(v.id)} disabled={reactivateMut.isPending}
                    style={{ ...outlineBtnStyle, color: "#16a34a", borderColor: "#86efac" }}>Reactivate</button>
                )}
              </div>
            </div>
          ))}
          {(vendors as any[]).length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
              No {showInactive ? "inactive" : "active"} vendors yet.
            </div>
          )}
        </div>
      )}

      {statementVendorId !== null && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}
          onClick={() => setStatementVendorId(null)}>
          <div className="modal-sheet" style={{ background: "white", borderRadius: 12, padding: 24, width: 560, maxHeight: "80vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}>
            {statementLoading && <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>Loading statement…</div>}
            {!statementLoading && statement && (
              <>
                <h3 style={{ margin: "0 0 4px", color: NAVY }}>{(statement as any).vendor.name}</h3>
                <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 13 }}>
                  Statement: {(statement as any).from} to {(statement as any).to}
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 18 }}>
                  <div style={{ background: "#f8fafc", borderRadius: 8, padding: 12 }}>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Total Billed</div>
                    <div style={{ fontWeight: 700, color: NAVY }}>{(statement as any).total_billed.toFixed(2)}</div>
                  </div>
                  <div style={{ background: "#f8fafc", borderRadius: 8, padding: 12 }}>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Total Paid</div>
                    <div style={{ fontWeight: 700, color: "#16a34a" }}>{(statement as any).total_paid.toFixed(2)}</div>
                  </div>
                  <div style={{ background: "#f8fafc", borderRadius: 8, padding: 12 }}>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Balance Outstanding</div>
                    <div style={{ fontWeight: 700, color: "#dc2626" }}>{(statement as any).balance_outstanding.toFixed(2)}</div>
                  </div>
                </div>
                <div style={{ display: "grid", gap: 6, fontSize: 13, color: "#475569" }}>
                  {(statement as any).bills.map((b: any) => (
                    <div key={b.id} style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f1f5f9", padding: "6px 0" }}>
                      <span>{b.bill_number}</span>
                      <span>{b.bill_date}</span>
                      <span style={{ textTransform: "capitalize" }}>{b.status}</span>
                      <span style={{ fontWeight: 600 }}>{b.net_payable.toFixed(2)}</span>
                    </div>
                  ))}
                  {(statement as any).bills.length === 0 && (
                    <div style={{ textAlign: "center", padding: "20px 0", color: "#94a3b8" }}>No bills in this range.</div>
                  )}
                </div>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setStatementVendorId(null)} style={outlineBtnStyle}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  background: "#1e3a8a",
  color: "white", border: "none", padding: "10px 20px",
  borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};
const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};
const labelStyle: React.CSSProperties = {
  display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569",
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
